import {Exclude, Expose, instanceToPlain, plainToInstance, Transform} from "class-transformer";
import * as t from "io-ts";
import {validateJSON, validateJSONNow} from "../../web/validation";
import {Entity} from "../entity";
import {RrcWasteType, toRrcWasteType} from "./rrc-waste-type";
import {getDatabaseCodeTransformer} from "../transfomers/transformers";
import {RrcWasteBasis, toRrcWasteBasis} from "./rrc-waste-basis";
import {RrcWastePhysicalCharacteristic, toRrcWastePhysicalCharacteristic} from "./rrc-waste-physical-characteristic";
import {RrcWasteClassification, toRrcWasteClassification} from "./rrc-waste-classification";

export const RrcEp9WasteProfileCodec = t.intersection([
  t.type({
    id: t.string,
    profileKey: t.string,
    effectiveStartDttm: t.number,
    version: t.number,
    status: t.string,
    rrcCompanyProfileId: t.string,
    objectId: t.string,
    objectType: t.string,
    wasteId: t.string,
    generatorOrgName: t.string,
    generatorP5Number: t.string,
    generatorCommercial: t.boolean,
    wasteType: t.string,
    wastePhysicalCharacteristics: t.string,
    wasteClassification: t.string,
    wasteChemicalCharacteristics: t.string,
    wasteConstituents: t.string,
    wasteBasis: t.string,
    snapshotJson: t.string,
    snapshotHash: t.string,
    createDttm: t.number,
    lastModifiedDttm: t.number,
  }),
  t.partial({
    supersedesId: t.string,
    supersededById: t.string,
    effectiveEndDttm: t.number,
    objectName: t.string,
    contactName: t.string,
    contactPhone: t.string,
    contactEmail: t.string,
    rrcLocationProfileId: t.string,
    generatorLocationFacilityName: t.string,
    generatorLocationLeaseName: t.string,
    generatorLocationLeaseNumber: t.string,
    generatorLocationGasPlantNumber: t.string,
    generatorLocationApiNumber: t.string,
    generatorLocationOtherIdentifier: t.string,
    generatorLocationLatCoord: t.number,
    generatorLocationLongCoord: t.number,
    generatorLocationCounty: t.string,
    rcraExempt: t.boolean,
    wasteNormReading: t.number,
    approvedById: t.string,
    approvedByName: t.string,
    approvedDttm: t.number,
    createdById: t.string,
    lastModifiedById: t.string,
    createdByName: t.string,
    lastModifiedByName: t.string,
    regionIds: t.array(t.string),
  }),
]);

export type RrcEp9WasteProfile = t.TypeOf<typeof RrcEp9WasteProfileCodec>;

@Exclude()
export class RrcEp9WasteProfileEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  profileKey!: string;

  @Expose()
  effectiveStartDttm!: number;

  @Expose()
  effectiveEndDttm?: number;

  @Expose()
  version!: number;

  @Expose()
  status!: string;

  @Expose()
  supersedesId?: string;

  @Expose()
  supersededById?: string;

  @Expose()
  rrcCompanyProfileId!: string;

  @Expose()
  rrcLocationProfileId!: string;

  @Expose()
  objectId!: string;

  @Expose()
  objectType!: string;

  @Expose()
  objectName?: string;

  @Expose()
  wasteId!: string;

  @Expose()
  contactName?: string;

  @Expose()
  contactPhone?: string;

  @Expose()
  contactEmail?: string;

  @Expose()
  generatorOrgName?: string;

  @Expose()
  generatorP5Number?: string;

  @Expose()
  generatorCommercial = false;

  @Expose()
  generatorLocationFacilityName?: string;

  @Expose()
  generatorLocationLeaseName?: string;

  @Expose()
  generatorLocationLeaseNumber?: string;

  @Expose()
  generatorLocationGasPlantNumber?: string;

  @Expose()
  generatorLocationApiNumber?: string;

  @Expose()
  generatorLocationOtherIdentifier?: string;

  @Expose()
  generatorLocationLatCoord?: number;

  @Expose()
  generatorLocationLongCoord?: number;

  @Expose()
  generatorLocationCounty?: string;

  @Transform(getDatabaseCodeTransformer(toRrcWasteType))
  @Expose()
  wasteType?: RrcWasteType;

  @Transform(getDatabaseCodeTransformer(toRrcWastePhysicalCharacteristic))
  @Expose()
  wastePhysicalCharacteristics!: RrcWastePhysicalCharacteristic;

  @Transform(getDatabaseCodeTransformer(toRrcWasteClassification))
  @Expose()
  wasteClassification!: RrcWasteClassification;

  @Expose()
  wasteChemicalCharacteristics!: string;

  @Expose()
  wasteConstituents!: string;

  @Transform(getDatabaseCodeTransformer(toRrcWasteBasis))
  @Expose()
  wasteBasis!: RrcWasteBasis;

  @Expose()
  rcraExempt?: boolean

  @Expose()
  wasteNormReading?: number

  @Expose()
  snapshotJson!: string;

  @Expose()
  snapshotHash!: string;

  @Expose()
  approvedById?: string;

  @Expose()
  approvedByName?: string;

  @Expose()
  approvedDttm?: number;

  @Expose()
  createdById?: string;

  @Expose()
  lastModifiedById?: string;

  @Expose()
  createDttm!: number;

  @Expose()
  lastModifiedDttm!: number;

  @Expose()
  regionIds?: string[];

  @Expose()
  createdByName?: string;

  @Expose()
  lastModifiedByName?: string;

  readonly entityType = 'RRCEP9WASTEPROFILE';

  get displayName(): Readonly<string> {
    return this.id;
  }

  /**
   * Constructor for RrcEp9WasteProfileEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  static from(value: RrcEp9WasteProfile): RrcEp9WasteProfileEntity {
    return plainToInstance(RrcEp9WasteProfileEntity, value, {
      excludeExtraneousValues: true,
    });
  }

  loadTypeValue(): Promise<RrcEp9WasteProfile> {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSON(RrcEp9WasteProfileCodec, object);
  }

  asTypeValue(): RrcEp9WasteProfile {
    const object = instanceToPlain(this, {
      excludeExtraneousValues: true,
    });
    return validateJSONNow(RrcEp9WasteProfileCodec, object);
  }
}

export interface RrcEp9WasteProfileCreateDTO {
  profileKey?: string;
  effectiveStartDttm: number;
  effectiveEndDttm?: number
  objectId: string;
  objectType: string;
  generatorLocationProfileKey?: string;
  generatorProfileKey?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  wasteType?: string
  wastePhysicalCharacteristics?: string
  wasteClassification?: string
  wasteNormReading?: number
  wasteChemicalCharacteristics?: string
  wasteConstituents?: string
  wasteBasis?: string
  rcraExempt?: boolean
}

export interface RrcEp9WasteProfileUpdateDTO {
  id: string
  effectiveStartDttm: number;
  effectiveEndDttm?: number
  objectId: string;
  objectType: string;
  generatorLocationProfileKey?: string;
  generatorProfileKey?: string;
  contactName?: string;
  contactPhone?: string;
  contactEmail?: string;
  wasteType?: string
  wastePhysicalCharacteristics?: string
  wasteClassification?: string
  wasteNormReading?: number
  wasteChemicalCharacteristics?: string
  wasteConstituents?: string
  wasteBasis?: string
  rcraExempt?: boolean
}
